import { useEffect, useRef, useState } from 'react';
import { useSaveProject } from '../../../hooks/use-save-project';

type UseAnimationPanelsOptions = {
  /** Whether the animation preview is currently available to the user. */
  isEnabled: boolean;
  projectId: string;
  /** Resolves true when the card changed since the last save. */
  hasUnsavedChanges: () => Promise<boolean>;
  /** Called after a successful render so the unsaved-changes baseline stays accurate. */
  onPanelsPrepared?: () => void;
};

/**
 * Prepares the front/inside panel assets the DG animation needs.
 *
 * Autosave only asks the backend for the `P` thumbnail, so until the user hits
 * "Done" a project has no `F`/`I` assets at all and `loadProjectDetails` finds
 * nothing to animate. Saving with `generateFEPreviews` renders, uploads and
 * links those panels - the same work the Done button performs - so the editor
 * quick preview can play the animation from real asset URLs.
 *
 * @returns whether the panels are still being prepared
 */
export const useAnimationPanels = ({
  isEnabled,
  projectId,
  hasUnsavedChanges,
  onPanelsPrepared,
}: UseAnimationPanelsOptions) => {
  const { saveProject } = useSaveProject();
  const [isPreparingPanels, setIsPreparingPanels] = useState(false);
  const preparedProjectIdRef = useRef<string>();

  // saveProject and the callbacks are rebuilt on every card edit, so they are
  // read from refs at fire time. Adding them to the dependency list below would
  // re-run this effect - and re-upload both panels - on every keystroke.
  const saveProjectRef = useRef(saveProject);
  saveProjectRef.current = saveProject;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const onPanelsPreparedRef = useRef(onPanelsPrepared);
  onPanelsPreparedRef.current = onPanelsPrepared;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let isStale = false;

    const preparePanels = async () => {
      // Rendering and uploading both panels is expensive, so it is skipped when
      // this project was already prepared and nothing has changed since.
      if (preparedProjectIdRef.current === projectId && !(await hasUnsavedChangesRef.current())) {
        return;
      }

      setIsPreparingPanels(true);

      try {
        await saveProjectRef.current({
          showLoadingScreen: false,
          shouldRestoreCanvas: true,
          generateFrontPreview: false,
          generateFEPreviews: true,
        });
        preparedProjectIdRef.current = projectId;
        onPanelsPreparedRef.current?.();
      } catch {
        // saveProject already surfaces its own toast/system error.
      } finally {
        if (!isStale) {
          setIsPreparingPanels(false);
        }
      }
    };

    void preparePanels();

    return () => {
      isStale = true;
    };
  }, [isEnabled, projectId]);

  return { isPreparingPanels };
};
